/**
 * Single source of truth for environment/config values.
 * Never read `import.meta.env` anywhere else in the app — always import
 * `env` from here. This keeps every "constant" configurable per deployment
 * (dev / staging / production) instead of hardcoded.
 *
 * `apiBaseUrl` has two layers, in increasing priority:
 *   1. the build-time default below, baked in by Vite from `VITE_API_BASE_URL`
 *      at `npm run build` time.
 *   2. `public/config.json` — copied as-is into the deployed `dist/` folder
 *      (Vite never processes files under `public/`), fetched once at app
 *      startup by `loadRuntimeConfig()` below. Ops can hand-edit this file
 *      directly on the server to change the API URL WITHOUT rebuilding.
 */

function readEnv(key: keyof ImportMetaEnv, fallback?: string): string {
  const value = import.meta.env[key] ?? fallback;
  if (value === undefined) {
    // Fail loudly in development instead of silently sending requests to `undefined`.
    return '';
  }
  return value;
}

export const env = {
  apiBaseUrl: readEnv('VITE_API_BASE_URL', ''),
  storagePrefix: readEnv('VITE_APP_STORAGE_PREFIX', 'deepvision_erp'),
};

interface RuntimeConfig {
  apiBaseUrl?: string;
}

/**
 * Fetches `config.json` from the deployed app's own root and, if it defines
 * `apiBaseUrl`, overrides the build-time default above.
 *
 * Must be awaited in main.tsx BEFORE anything that reads `env.apiBaseUrl` is
 * imported (apiClient.ts, assetUrl.ts, ...) — those capture the value at
 * module-load time, so an override that arrives after they've already run
 * would be too late. main.tsx enforces this by dynamically importing `App`
 * only once this promise resolves.
 *
 * A missing file (e.g. local dev, or a deployment that never added one), a
 * network hiccup, or invalid JSON all fail silently — the build-time default
 * keeps working, so a broken/absent config.json can never break the app.
 */
export async function loadRuntimeConfig(): Promise<void> {
  try {
    const res = await fetch(`${import.meta.env.BASE_URL}config.json`, { cache: 'no-store' });
    if (!res.ok) return;
    const config = (await res.json()) as RuntimeConfig;
    if (config.apiBaseUrl) {
      env.apiBaseUrl = config.apiBaseUrl;
    }
  } catch {
    // No config.json / offline / malformed — keep the build-time default.
  }
}
